'use client'

import type { Space } from '@cocoonspace/sdk-js/types/space'
import { forwardRef } from 'react'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { TwicImg, TwicPicture } from '@twicpics/components/react'
import { ImageIcon } from 'lucide-react'

import { ShareBox } from '~/components/molecules/share-box'
import { SpaceDetailsImageCarousel } from '~/domains/spaces/components/space-details/space-details-images-carousel'
import { SpaceFavoriteButton } from '../atoms/space-favorite-button'
import { SpacePhotosDialog } from '../organisms/space-dialog-carousel'

const SeePhotosButton = forwardRef<
	HTMLButtonElement,
	React.ComponentPropsWithoutRef<typeof Button>
>(({ className, ...props }, ref) => {
	const { t } = useLingui()

	return (
		<Button
			ref={ref}
			size='sm'
			variant='default'
			className={cn(
				'text-foreground absolute bottom-4 left-4 h-[36px] bg-white/70 !p-3 hover:text-white',
				className,
			)}
			{...props}
		>
			<ImageIcon
				size={16}
				strokeWidth={1.5}
			/>

			{t`Voir les photos`}
		</Button>
	)
})

const SpaceHeroImage = ({ src, alt }: { src: string; alt: string }) => (
	<TwicPicture
		aria-label={alt}
		src={src}
		alt={alt}
		eager
		preTransform='quality=80'
		mode='cover'
		ratio='720/400'
		focus='50px100p'
		sizes='(min-width: 768px) calc(100vw - 370px - 5rem), 100vw'
	/>
)

const SpaceImage = ({ src, alt }: { src: string; alt: string }) => (
	<TwicImg
		aria-label={alt}
		src={src}
		alt={alt}
		step={1}
		preTransform='quality=80'
		mode='cover'
		ratio='720/400'
		focus='50px100p'
	/>
)

export const QuickActions = ({
	space,
	className,
}: {
	space: Space
	className?: string
}) => {
	const { t } = useLingui()

	return (
		<Card
			className={cn(
				'text-foreground absolute top-4 right-4 z-10 flex gap-1 rounded-md bg-white/70 p-1',
				className,
			)}
		>
			<SpaceFavoriteButton space={space} />

			<ShareBox
				title={t`Partager ce Cocoon`}
				buttonProps={{
					size: 'sm',
					className: 'hover:bg-primary hover:text-primary-foreground',
				}}
			/>
		</Card>
	)
}

export const SpaceDetailsPhotos = forwardRef<HTMLDivElement, { space?: Space }>(
	({ space }, ref) => {
		if (!space) return null

		return (
			<div className='relative'>
				<div className='relative -mx-6 overflow-hidden lg:hidden'>
					<QuickActions space={space} />
					<SpaceDetailsImageCarousel space={space} />
				</div>

				<div
					ref={ref}
					className='hidden h-[392px] gap-4 overflow-hidden md:flex'
				>
					<SpacePhotosDialog space={space}>
						<Card className='relative flex-1 shrink-0 cursor-pointer overflow-hidden'>
							<SpaceHeroImage
								src={space?.images[0]?.path as string}
								alt={space?.title}
							/>

							<SpacePhotosDialog space={space}>
								<SeePhotosButton />
							</SpacePhotosDialog>

							<QuickActions space={space} />
						</Card>
					</SpacePhotosDialog>

					<div className='flex w-[370px] flex-col gap-4'>
						{space?.images?.slice(1, 3).map((image, i) => (
							<SpacePhotosDialog
								key={image.id}
								activeIndex={i + 1}
								space={space}
							>
								<Card className='relative h-[250px] cursor-pointer overflow-hidden'>
									<SpaceImage
										src={image.path}
										alt={space?.title}
									/>
								</Card>
							</SpacePhotosDialog>
						))}
					</div>
				</div>
			</div>
		)
	},
)

SpaceDetailsPhotos.displayName = 'SpaceDetailsPhotos'
